import { createSlice } from "@reduxjs/toolkit";
import { api } from "../api";
import config from "../../../utils/config";

export const tileLayoutMapper = {
  rak: "simple",
  vinkel_hoger: "right",
  vinkel_vanster: "left",
};

const initialState = {
  /** Current selected tile shape */
  tileShape: "simple",
  lineAlignment: "standard",
  currentEvent: "shape",
  canvas: {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  },
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  restrictions: {
    minHeight: 0,
    maxHeight: 0,
    minWidth: 0,
    maxWidth: 0,
    jointPosWidth: 0,
  },
  texture: {},
  thickness: "",
  selectedTile: {},
  selectedVariation: {},
};

const getShapeInputValues = (tileShape, selectedTile) => {
  const {
    settings: { configuration },
  } = selectedTile;

  switch (tileShape) {
    case "simple": {
      const {
        horizontal: {
          default_width,
          default_height,
          minimum_height: minHeight,
          maximum_height: maxHeight,
          minimum_width: minWidth,
          maximum_width: maxWidth,
          joint_pos_max: jointPosWidth,
        },
      } = configuration;
      return {
        top: default_width,
        left: default_height,
        right: default_width / 2,
        bottom: default_width / 2,
        restrictions: {
          minHeight,
          maxHeight,
          minWidth,
          maxWidth,
          jointPosWidth,
        },
      };
    }
    case "left":
    case "right": {
      const isRightTile = tileShape === "right";

      const {
        horizontal: {
          default_bottom_width,
          default_left_height,
          default_right_height,
          default_top_width,
          minimum_height: minHeight,
          maximum_height: maxHeight,
          minimum_width: minWidth,
          maximum_width: maxWidth,
        },
      } = configuration;
      return {
        top: default_top_width,
        left: isRightTile ? default_right_height : default_left_height,
        right: isRightTile ? default_left_height : default_right_height,
        bottom: default_bottom_width,
        restrictions: {
          minHeight,
          maxHeight,
          minWidth,
          maxWidth,
        },
      };
    }
    default:
      throw new Error(`${tileShape} doesn't exists`);
  }
};

export const dimensionsSlice = createSlice({
  name: "dimensions",
  initialState,
  reducers: {
    changeTileShape(state, action) {
      const { tileShape, selectedTile } = action.payload;
      const mappedTileShape = tileLayoutMapper[tileShape];
      const inputValues = getShapeInputValues(mappedTileShape, selectedTile);
      state = {
        ...state,
        tileShape: mappedTileShape,
        ...inputValues,
        selectedTile: { ...selectedTile, name: tileShape },
      };
      return state;
    },
    changeLineAlignment(state, action) {
      state.lineAlignment = action.payload;
    },
    changeTop(state, action) {
      state.top = action.payload;
    },
    changeLeft(state, action) {
      state.left = action.payload;
    },
    changeBottom(state, action) {
      state.bottom = action.payload;
    },
    changeRight(state, action) {
      state.right = action.payload;
    },
    changeTexture(state, action) {
      state.texture = action.payload;
    },
    changeThickness(state, action) {
      state.thickness = action.payload;
    },
    changeVariation(state, action) {
      state.selectedVariation = action.payload;
    },
    changeCurrentEvent(state, action) {
      state.currentEvent = action.payload;
    },
  },
  extraReducers: (builder) => {
    /** By Default, on builder data field set simple  */
    builder.addMatcher(
      api.endpoints.getBuilderData.matchFulfilled,
      (state, { payload }) => {
        /** Rak is simple tile */
        const inputValues = getShapeInputValues(
          "simple",
          payload.selected?.bankskiva?.rak
        );

        const { thickness_with_price } =
          payload.selected?.bankskiva?.rak?.settings;
        const { variations } = payload.selected?.bankskiva?.rak;
        const defaultThickness = thickness_with_price && thickness_with_price.find(x => x.thickness === config.defaultThickness)
        state = {
          ...state,
          ...inputValues,
          texture: payload.selected,
          thickness: defaultThickness ? defaultThickness.thickness :  thickness_with_price[0]?.thickness,
          selectedTile: { ...payload.selected?.bankskiva?.rak, name: "rak" },
          selectedVariation: variations[1],
        };
        return state;
      }
    );
  },
});

/** Actions */
export const {
  changeTileShape,
  changeLineAlignment,
  changeBottom,
  changeLeft,
  changeRight,
  changeTop,
  changeTexture,
  changeVariation,
  changeThickness,
  changeCurrentEvent,
} = dimensionsSlice.actions;

/** Selectors */
export const getCurrentEvent = (store) => store.dimensions.currentEvent;
export const getTileShape = (store) => store.dimensions.tileShape;
export const getLineAlignment = (store) => store.dimensions.lineAlignment;
export const getDimensions = (store) => store.dimensions;
export const getSelectedTile = (store) => store.dimensions.selectedTile;
export const getSelectedTexture = (store) => store.dimensions.texture;
export const getThickness = (store) => store.dimensions.thickness;
export const getSelectedVariation = (store) =>
  store.dimensions.selectedVariation;
